import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { toast } from 'sonner';
import { CrewForm, type CrewFormValues } from '@/components/organisms/crews/CrewForm';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useSchedulerCrews } from '@/hooks/scheduler/useSchedulerCrews';
import { useSchedulerTechnicians } from '@/hooks/scheduler/useSchedulerTechnicians';
import { apiPatch, apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerCrew } from '@/types/scheduler';

export const Route = createFileRoute('/_dashboard/dashboard/crews/new')({
  component: NewCrewPage,
});

async function syncCrewMembers(crewId: string, technicianIds: string[]) {
  await Promise.all(
    technicianIds.map((technicianId) =>
      apiPost<void>(operationsClient, schedulerEndpoints.crewAssign(crewId), { technicianId }),
    ),
  );
}

function NewCrewPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const crewsQuery = useSchedulerCrews();
  const techniciansQuery = useSchedulerTechnicians();

  const createMutation = useApiMutation<SchedulerCrew, CrewFormValues>({
    mutationFn: async (values) => {
      const crew = await apiPost<SchedulerCrew>(operationsClient, schedulerEndpoints.crews(), {
        name: values.name,
        timezone: values.timezone,
      });
      await syncCrewMembers(crew.id, values.technicianIds);
      // color/active + lead AFTER members exist — the backend requires the lead to be a member.
      const finalCrew = await apiPatch<SchedulerCrew>(
        operationsClient,
        schedulerEndpoints.crew(crew.id),
        {
          color: values.color,
          isActive: values.isActive,
          leadTechnicianId: values.leadTechnicianId,
        },
      );
      return finalCrew;
    },
    onSuccess: (crew) => {
      toast.success('Crew created');
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.crews() });
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
      void router.navigate({
        to: '/dashboard/crews/$id',
        params: { id: crew.id },
        search: true,
      });
    },
  });

  return (
    <CrewForm
      mode="create"
      crews={crewsQuery.data ?? []}
      technicians={techniciansQuery.data ?? []}
      loadingOptions={crewsQuery.isLoading || techniciansQuery.isLoading}
      submitting={createMutation.isPending}
      onBack={() => void router.navigate({ to: '/dashboard/crews', search: true })}
      onSubmit={(values) => createMutation.mutate(values)}
    />
  );
}
